import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
	Button,
	Card,
	Col,
	Drawer,
	Divider,
	Form,
	Input,
	InputNumber,
	Modal,
	Pagination,
	Radio,
	Row,
	Select,
	Space,
	Spin,
	Switch,
	Table,
	Tabs,
	Tag,
	Typography,
	Upload,
	message,
	Tooltip,
} from 'antd';
import { CloudUploadOutlined, EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';

import Constant from '../../lib/constant';
import axiosClient from '../../api/axiosClient';
import sectionApiV2 from '../../api/secitonV2';

// CKEditor
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';

const { Title, Text } = Typography;
const { TextArea } = Input;

const API_BASE = (Constant.apiUrl || '').replace(/\/$/, '');

const SECTION_TYPES = [
	'CONTENT_HTML',
	'DOWNLOAD_LIST',
	'PROJECT_INTRO_2BLOCK',
	'PRODUCT_SWIPER',
	'NEWS_SWIPER',
	'NEWS_HERO_PLUS_3',
	'CATEGORY_GRID',
	'LINK_CARDS',
];

export const SECTION_TYPE_LABEL = {
	CONTENT_HTML: 'Nội dung (văn bản / bài viết)',
	DOWNLOAD_LIST: 'Danh sách tải về (file)',
	PROJECT_INTRO_2BLOCK: 'Giới thiệu (2 khối hình + nội dung)',
	PRODUCT_SWIPER: 'Sản phẩm dạng trượt (slider)',
	NEWS_SWIPER: 'Tin tức dạng trượt (slider)',
	NEWS_HERO_PLUS_3: 'Tin nổi bật + 3 tin',
	CATEGORY_GRID: 'Lưới danh mục',
	LINK_CARDS: 'Thẻ liên kết (banner/link)',
};

export const SECTION_TYPE_OPTIONS = [
	{ label: SECTION_TYPE_LABEL.CONTENT_HTML, value: 'CONTENT_HTML' },
	{ label: SECTION_TYPE_LABEL.DOWNLOAD_LIST, value: 'DOWNLOAD_LIST' },
	{ label: SECTION_TYPE_LABEL.PROJECT_INTRO_2BLOCK, value: 'PROJECT_INTRO_2BLOCK' },
	{ label: SECTION_TYPE_LABEL.PRODUCT_SWIPER, value: 'PRODUCT_SWIPER' },
	{ label: SECTION_TYPE_LABEL.NEWS_SWIPER, value: 'NEWS_SWIPER' },
	{ label: SECTION_TYPE_LABEL.NEWS_HERO_PLUS_3, value: 'NEWS_HERO_PLUS_3' },
	{ label: SECTION_TYPE_LABEL.CATEGORY_GRID, value: 'CATEGORY_GRID' },
	{ label: SECTION_TYPE_LABEL.LINK_CARDS, value: 'LINK_CARDS' },
];

const renderTypeTag = (type) => (
	<Tooltip title={type}>
		<Tag color={getTypeTagColor(type)} style={{ marginRight: 0, borderRadius: 999 }}>
			{SECTION_TYPE_LABEL[type] || type}
		</Tag>
	</Tooltip>
);

const getTypeTagColor = (type) => {
	const map = {
		CONTENT_HTML: 'magenta',
		DOWNLOAD_LIST: 'volcano',
		PROJECT_INTRO_2BLOCK: 'geekblue',
		PRODUCT_SWIPER: 'blue',
		NEWS_SWIPER: 'purple',
		NEWS_HERO_PLUS_3: 'gold',
		CATEGORY_GRID: 'gold',
		LINK_CARDS: 'green',
	};

	return map[type] || 'blue';
};

const LANGS = [
	{ key: 'vi', label: 'VI' },
	{ key: 'en', label: 'EN' },
];

// ============================
// Helpers
// ============================
function safeObj(v) {
	return v && typeof v === 'object' ? v : {};
}
function normalizeI18n(val) {
	const o = safeObj(val);
	return { vi: o.vi ?? '', en: o.en ?? '' };
}
function normalizeImage(img) {
	if (!img) return null;
	const o = safeObj(img);
	const any = o.url || o.public_id || o.signature;
	if (!any) return null;
	return {
		url: o.url || '',
		public_id: o.public_id || '',
		signature: o.signature || '',
	};
}
function isHtmlEmpty(html) {
	if (!html) return true;
	const s = String(html)
		.replace(/<style[\s\S]*?<\/style>/gi, '')
		.replace(/<script[\s\S]*?<\/script>/gi, '')
		.replace(/<[^>]+>/g, '')
		.replace(/&nbsp;/g, ' ')
		.trim();
	return s.length === 0;
}
function getIn(obj, path) {
	return path.reduce((acc, k) => (acc ? acc[k] : undefined), obj);
}
function pickLang(val, lang = 'vi') {
	if (!val) return '';
	if (typeof val === 'string') return val;
	if (typeof val === 'object') {
		if ('vi' in val || 'en' in val) return val?.[lang] || val?.vi || val?.en || '';
		const v = Object.values(val)[0];
		return typeof v === 'string' ? v : '';
	}
	return '';
}
function setFieldValueCompat(form, namePath, value) {
	if (typeof form?.setFieldValue === 'function') {
		form.setFieldValue(namePath, value);
		return;
	}
	form.setFields([{ name: namePath, value }]);
}
function mergeI18nField(form, namePath, langKey, nextValue) {
	const latest = form.getFieldValue(namePath) || { vi: '', en: '' };
	const next = { ...latest, [langKey]: nextValue };
	setFieldValueCompat(form, namePath, next);
}
function uniqStrings(arr) {
	if (!Array.isArray(arr)) return [];
	return Array.from(new Set(arr.filter(Boolean).map(String)));
}

// ============================
// Admin endpoints
// ============================
async function apiAdminGetSectionById(id) {
	const res = await axiosClient.get(`${API_BASE}/sections/admin/${id}`);
	return res;
}
async function apiAdminListPages(params) {
	const res = await axiosClient.get(`${API_BASE}/pages/admin`, { params });
	return res;
}
async function apiAdminListProject(params) {
	const res = await axiosClient.get(`${API_BASE}/project/admin`, { params });
	return res;
}
async function apiAdminListProductCategories(params) {
	const res = await axiosClient.get(`${API_BASE}/product-category/admin`, { params });
	return res;
}
async function apiAdminListNewsCategories(params) {
	const res = await axiosClient.get(`${API_BASE}/newsCategory/admin`, { params });
	return res;
}
function extractList(res) {
	const data = res?.data?.data || res?.data || {};
	return {
		items: data?.items || [],
		pagination: data?.pagination || {},
	};
}

// ============================
// Upload API
// ============================
async function apiUploadCloudImage(file, uploadFolder = 'images') {
	const form = new FormData();
	form.append('file', file);
	form.append('uploadFolder', uploadFolder);

	const res = await axiosClient.post(`${API_BASE}/common/upload/cloud/image`, form, {
		headers: { 'Content-Type': 'multipart/form-data' },
	});

	return res?.data?.data || res?.data || res;
}
async function apiUploadCloudFile(file, uploadFolder = 'files') {
	const form = new FormData();
	form.append('file', file);
	form.append('uploadFolder', uploadFolder);

	const res = await axiosClient.post(`${API_BASE}/common/upload/file/cloud`, form, {
		headers: { 'Content-Type': 'multipart/form-data' },
	});

	return res?.data?.data || res?.data || res;
}

// ============================
// Normalize section <-> form
// ============================
function normalizeSectionForForm(sec) {
	const s = sec || {};
	const props = safeObj(s.props);
	const t = s.type;
	let nextProps = props;

	if (t === 'CONTENT_HTML') {
		nextProps = {
			title: normalizeI18n(props.title),
			description: normalizeI18n(props.description),
			html: normalizeI18n(props.html),
			boxed: props.boxed ?? true,
		};
	}

	if (t === 'DOWNLOAD_LIST') {
		nextProps = {
			title: normalizeI18n(props.title),
			description: normalizeI18n(props.description),
			items: Array.isArray(props.items)
				? props.items.map((it) => ({
						names: normalizeI18n(it?.names),
						file: {
							url: it?.file?.url || '',
							originalName: it?.file?.originalName || '',
							mime: it?.file?.mime || '',
							size: it?.file?.size ?? undefined,
						},
					}))
				: [],
		};
	}

	if (t === 'PROJECT_INTRO_2BLOCK') {
		const normBlock = (b) => {
			const bb = safeObj(b);
			return {
				enabled: bb.enabled ?? true,
				image: normalizeImage(bb.image),
				heading: normalizeI18n(bb.heading),
				html: normalizeI18n(bb.html),
			};
		};

		nextProps = {
			title: normalizeI18n(props.title),
			description: normalizeI18n(props.description),
			boxed: props.boxed ?? true,
			top: normBlock(props.top),
			bottom: normBlock(props.bottom),
		};
	}

	if (t === 'PRODUCT_SWIPER') {
		const q = safeObj(props.query);
		nextProps = {
			title: normalizeI18n(props.title),
			description: normalizeI18n(props.description),
			query: {
				projectId: q.projectId ?? null,
				productCategoryIds: Array.isArray(q.productCategoryIds) ? q.productCategoryIds : [],
				defaultPage: q.defaultPage ?? 1,
				defaultLimit: q.defaultLimit ?? 12,
			},
		};
	}

	if (t === 'NEWS_SWIPER') {
		const q = safeObj(props.query);
		nextProps = {
			title: normalizeI18n(props.title),
			description: normalizeI18n(props.description),
			query: {
				projectId: q.projectId ?? null,
				newsCategoryIds: Array.isArray(q.newsCategoryIds) ? q.newsCategoryIds : [],
				defaultPage: q.defaultPage ?? 1,
				defaultLimit: q.defaultLimit ?? 12,
			},
		};
	}

	if (t === 'NEWS_HERO_PLUS_3') {
		const q = safeObj(props.query);
		nextProps = {
			title: normalizeI18n(props.title),
			description: normalizeI18n(props.description),
			query: {
				projectId: q.projectId ?? null,
				newsCategoryIds: Array.isArray(q.newsCategoryIds) ? q.newsCategoryIds : [],
				defaultPage: q.defaultPage ?? 1,
				defaultLimit: q.defaultLimit ?? 4,
			},
		};
	}

	if (t === 'CATEGORY_GRID') {
		nextProps = {
			title: normalizeI18n(props.title),
			description: normalizeI18n(props.description),
			kind: props.kind || 'product',
			projectId: props.projectId ?? null,
			basePath: props.basePath ?? '',
		};
	}

	if (t === 'LINK_CARDS') {
		nextProps = {
			title: normalizeI18n(props.title),
			description: normalizeI18n(props.description),
			items: Array.isArray(props.items)
				? props.items.map((it) => ({
						name: normalizeI18n(it?.name),
						image: normalizeImage(it?.image),
						href: it?.href || '',
						pageId: it?.pageId ?? null,
						linkType: it?.pageId ? 'page' : 'url',
					}))
				: [],
		};
	}

	return {
		_id: s._id,
		name: s.name || '',
		type: s.type || 'CONTENT_HTML',
		tags: Array.isArray(s.tags) ? s.tags : [],
		props: nextProps,
	};
}

function buildPayloadFromForm(values) {
	const v = values || {};
	const type = v.type;

	const payload = {
		name: v.name || '',
		type,
		tags: Array.isArray(v.tags) ? v.tags : [],
		props: v.props ?? {},
	};

	const p = safeObj(payload.props);
	const pickI18n = (x) => normalizeI18n(x);
	const pickImage = (img) => {
		const o = normalizeImage(img);
		return o ? o : null;
	};

	if (type === 'CONTENT_HTML') {
		payload.props = {
			title: pickI18n(p.title),
			description: pickI18n(p.description),
			html: pickI18n(p.html),
			boxed: p.boxed ?? true,
		};
	}

	if (type === 'DOWNLOAD_LIST') {
		payload.props = {
			title: pickI18n(p.title),
			description: pickI18n(p.description),
			items: Array.isArray(p.items)
				? p.items
						.filter((x) => x?.file?.url)
						.map((it) => ({
							names: pickI18n(it.names),
							file: {
								url: it.file.url,
								originalName: it.file.originalName || '',
								mime: it.file.mime || '',
								size: typeof it.file.size === 'number' ? it.file.size : undefined,
							},
						}))
				: [],
		};
	}

	if (type === 'PROJECT_INTRO_2BLOCK') {
		const pickBlock = (b) => {
			const bb = safeObj(b);
			return {
				enabled: bb.enabled ?? true,
				image: pickImage(bb.image),
				heading: pickI18n(bb.heading),
				html: pickI18n(bb.html),
			};
		};

		payload.props = {
			title: pickI18n(p.title),
			description: pickI18n(p.description),
			boxed: p.boxed ?? true,
			top: pickBlock(p.top),
			bottom: pickBlock(p.bottom),
		};
	}

	if (type === 'PRODUCT_SWIPER') {
		const q = safeObj(p.query);
		payload.props = {
			title: pickI18n(p.title),
			description: pickI18n(p.description),
			query: {
				projectId: q.projectId ?? null,
				productCategoryIds: uniqStrings(q.productCategoryIds),
				defaultPage: q.defaultPage ?? 1,
				defaultLimit: q.defaultLimit ?? 12,
			},
		};
	}

	if (type === 'NEWS_SWIPER') {
		const q = safeObj(p.query);
		payload.props = {
			title: pickI18n(p.title),
			description: pickI18n(p.description),
			query: {
				projectId: q.projectId ?? null,
				newsCategoryIds: uniqStrings(q.newsCategoryIds),
				defaultPage: q.defaultPage ?? 1,
				defaultLimit: q.defaultLimit ?? 12,
			},
		};
	}

	if (type === 'NEWS_HERO_PLUS_3') {
		const q = safeObj(p.query);
		payload.props = {
			title: pickI18n(p.title),
			description: pickI18n(p.description),
			query: {
				projectId: q.projectId ?? null,
				newsCategoryIds: uniqStrings(q.newsCategoryIds),
				defaultPage: q.defaultPage ?? 1,
				defaultLimit: q.defaultLimit ?? 4,
			},
		};
	}

	if (type === 'CATEGORY_GRID') {
		payload.props = {
			title: pickI18n(p.title),
			description: pickI18n(p.description),
			kind: p.kind || 'product',
			projectId: p.projectId ?? null,
			basePath: p.basePath ?? '',
		};
	}

	if (type === 'LINK_CARDS') {
		payload.props = {
			title: pickI18n(p.title),
			description: pickI18n(p.description),
			items: Array.isArray(p.items)
				? p.items
						.filter((x) => x?.href || x?.pageId)
						.map((it) => ({
							name: pickI18n(it.name),
							image: pickImage(it?.image || it?.image?.url || ''),
							href: it.href || '',
							pageId: it.pageId ?? null,
						}))
				: [],
		};
	}

	return payload;
}

function makeDefaultProps(type) {
	const base = { title: { vi: '', en: '' }, description: { vi: '', en: '' } };

	if (type === 'CONTENT_HTML') return { ...base, html: { vi: '', en: '' }, boxed: true };
	if (type === 'DOWNLOAD_LIST') return { ...base, items: [] };
	if (type === 'PROJECT_INTRO_2BLOCK')
		return {
			...base,
			boxed: true,
			top: { enabled: true, image: null, heading: { vi: '', en: '' }, html: { vi: '', en: '' } },
			bottom: { enabled: true, image: null, heading: { vi: '', en: '' }, html: { vi: '', en: '' } },
		};

	if (type === 'PRODUCT_SWIPER') {
		return {
			...base,
			query: { projectId: null, productCategoryIds: [], defaultPage: 1, defaultLimit: 12 },
		};
	}

	if (type === 'NEWS_SWIPER') {
		return {
			...base,
			query: { projectId: null, newsCategoryIds: [], defaultPage: 1, defaultLimit: 12 },
		};
	}

	if (type === 'NEWS_HERO_PLUS_3') {
		return {
			...base,
			query: { projectId: null, newsCategoryIds: [], defaultPage: 1, defaultLimit: 4 },
		};
	}

	if (type === 'CATEGORY_GRID') return { ...base, kind: 'product', projectId: null, basePath: '' };
	if (type === 'LINK_CARDS') return { ...base, items: [] };

	return base;
}

// ============================
// CKEditor upload plugin
// ============================
function uploadPlugin(editor) {
	editor.plugins.get('FileRepository').createUploadAdapter = (loader) => ({
		upload: async () => {
			const file = await loader.file;
			if (!file) throw new Error('No file');
			const data = await apiUploadCloudImage(file, 'images');
			if (!data?.url) throw new Error('Upload failed (missing url)');
			return { default: data.url };
		},
		abort: () => {},
	});
}
const CKEDITOR_CONFIG = { extraPlugins: [uploadPlugin] };

// ============================
// CKEditor Field (anti-loop)
// ============================
const CkEditorField = React.memo(function CkEditorField({ value = '', onChange }) {
	const editorRef = useRef(null);
	const destroyedRef = useRef(false);
	const settingRef = useRef(false);

	const lastValueRef = useRef(value ?? '');
	const pendingValueRef = useRef(null);
	const timerRef = useRef(null);
	const onChangeRef = useRef(onChange);

	useEffect(() => {
		onChangeRef.current = onChange;
	}, [onChange]);

	useEffect(() => {
		destroyedRef.current = false;
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
			const pending = pendingValueRef.current;
			if (pending != null && pending !== lastValueRef.current) {
				lastValueRef.current = pending;
				try {
					onChangeRef.current?.(pending);
				} catch {}
			}
			destroyedRef.current = true;
			editorRef.current = null;
		};
	}, []);

	useEffect(() => {
		const editor = editorRef.current;
		const next = value ?? '';
		lastValueRef.current = next;

		if (!editor || destroyedRef.current) return;

		let current = '';
		try {
			current = editor.getData?.() ?? '';
		} catch {
			return;
		}
		if (current === next) return;

		settingRef.current = true;
		try {
			editor.setData(next);
		} catch {
		} finally {
			Promise.resolve().then(() => (settingRef.current = false));
		}
	}, [value]);

	const handleChange = (_, editor) => {
		if (!editor || destroyedRef.current) return;
		if (settingRef.current) return;

		let data = '';
		try {
			data = editor.getData();
		} catch {
			return;
		}

		pendingValueRef.current = data;

		if (timerRef.current) clearTimeout(timerRef.current);
		timerRef.current = setTimeout(() => {
			if (destroyedRef.current) return;
			if (data === lastValueRef.current) return;
			lastValueRef.current = data;
			onChangeRef.current?.(data);
		}, 60);
	};

	return (
		<CKEditor
			editor={ClassicEditor}
			config={CKEDITOR_CONFIG}
			data={value ?? ''}
			onReady={(editor) => {
				editorRef.current = editor;
				try {
					const init = value ?? '';
					const cur = editor.getData?.() ?? '';
					if (cur !== init) editor.setData(init);
				} catch {}
			}}
			onChange={handleChange}
		/>
	);
});

// ============================
// I18N components
// ============================
function I18nTabsInputSafe({ form, label, namePath, formNamePath, required = false, input = 'input', rows = 4 }) {
	const itemName = formNamePath || namePath;

	return (
		<Form.Item label={label} required={required} style={{ marginBottom: 12 }}>
			<Form.Item noStyle shouldUpdate={(p, c) => getIn(p, namePath) !== getIn(c, namePath)}>
				{() => {
					const curObj = form.getFieldValue(namePath) || { vi: '', en: '' };

					return (
						<Tabs
							size="small"
							destroyOnHidden={false}
							animated={false}
							items={LANGS.map((l) => ({
								key: l.key,
								label: l.label,
								children:
									input === 'textarea' ? (
										<TextArea
											rows={rows}
											value={curObj?.[l.key] ?? ''}
											placeholder={`${label} (${l.label})`}
											onChange={(e) => mergeI18nField(form, namePath, l.key, e.target.value)}
										/>
									) : (
										<Input
											value={curObj?.[l.key] ?? ''}
											placeholder={`${label} (${l.label})`}
											onChange={(e) => mergeI18nField(form, namePath, l.key, e.target.value)}
										/>
									),
							}))}
						/>
					);
				}}
			</Form.Item>

			{required ? (
				<Form.Item
					name={itemName}
					hidden
					rules={[
						{
							validator: (_, v) =>
								(v?.vi ?? '').trim()
									? Promise.resolve()
									: Promise.reject(new Error(`Nhập ${label} (VI)`)),
						},
					]}
				>
					<Input />
				</Form.Item>
			) : null}
		</Form.Item>
	);
}

function I18nTabsEditorSafe({ form, label, namePath, formNamePath, required = false }) {
	const itemName = formNamePath || namePath;

	return (
		<Form.Item label={label} required={required} style={{ marginBottom: 12 }}>
			<Form.Item noStyle shouldUpdate={(p, c) => getIn(p, namePath) !== getIn(c, namePath)}>
				{() => {
					const curObj = form.getFieldValue(namePath) || { vi: '', en: '' };

					return (
						<Tabs
							size="small"
							destroyOnHidden={true}
							animated={false}
							items={LANGS.map((l) => ({
								key: l.key,
								label: l.label,
								children: (
									<CkEditorField
										key={`${namePath.join('.')}.${l.key}`}
										value={curObj?.[l.key] ?? ''}
										onChange={(val) => mergeI18nField(form, namePath, l.key, val)}
									/>
								),
							}))}
						/>
					);
				}}
			</Form.Item>

			{required ? (
				<Form.Item
					name={itemName}
					hidden
					rules={[
						{
							validator: (_, v) =>
								isHtmlEmpty(v?.vi)
									? Promise.reject(new Error(`Nhập ${label} (VI)`))
									: Promise.resolve(),
						},
					]}
				>
					<Input />
				</Form.Item>
			) : null}
		</Form.Item>
	);
}

function ImagePicker({ form, namePath, formNamePath, label = 'Image' }) {
	const itemName = formNamePath || namePath;

	const uploadProps = {
		multiple: false,
		maxCount: 1,
		showUploadList: false,
		customRequest: async (options) => {
			const { file, onSuccess, onError } = options;
			try {
				const data = await apiUploadCloudImage(file, 'images');
				setFieldValueCompat(form, namePath, {
					url: data?.url || '',
					public_id: data?.public_id || '',
					signature: data?.signature || '',
				});
				message.success('Upload ảnh thành công');
				onSuccess?.(data);
			} catch (e) {
				message.error(e?.message || 'Upload ảnh lỗi');
				onError?.(e);
			}
		},
	};

	return (
		<Card size="small" style={{ marginBottom: 12 }} title={label}>
			<Space direction="vertical" style={{ width: '100%' }} size={10}>
				<Upload {...uploadProps}>
					<Button icon={<CloudUploadOutlined />}>Upload Cloud Image</Button>
				</Upload>

				<Row gutter={12}>
					<Col span={24}>
						<Form.Item name={[...itemName, 'url']} label="image.url" style={{ marginBottom: 10 }}>
							<Input placeholder="https://..." />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={[...itemName, 'public_id']}
							label="image.public_id"
							style={{ marginBottom: 0 }}
						>
							<Input />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={[...itemName, 'signature']}
							label="image.signature"
							style={{ marginBottom: 0 }}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>

				<Form.Item noStyle shouldUpdate>
					{() => {
						const url = form.getFieldValue([...namePath, 'url']);
						return url ? (
							<div style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: 10 }}>
								<Text type="secondary">Preview</Text>
								<div style={{ marginTop: 8 }}>
									<img
										alt="preview"
										src={url}
										style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 10 }}
									/>
								</div>
							</div>
						) : null;
					}}
				</Form.Item>
			</Space>
		</Card>
	);
}

// ============================
// Preview
// ============================
function PreviewSection({ type, props, lang = 'vi' }) {
	const title = pickLang(props?.title, lang);
	const desc = pickLang(props?.description, lang);

	const commonHeader = (
		<div style={{ marginBottom: 12 }}>
			{title ? (
				<Title level={4} style={{ margin: 0 }}>
					{title}
				</Title>
			) : null}
			{desc ? <div style={{ marginTop: 6, color: '#556' }} dangerouslySetInnerHTML={{ __html: desc }} /> : null}
			<Divider style={{ margin: '12px 0' }} />
		</div>
	);

	if (!type) return <Text type="secondary">Chọn type để xem preview</Text>;

	if (type === 'CONTENT_HTML') {
		const html = pickLang(props?.html, lang);
		return (
			<div>
				{commonHeader}
				{html ? (
					<div style={{ lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: html }} />
				) : (
					<Text type="secondary">Chưa có HTML</Text>
				)}
			</div>
		);
	}

	if (type === 'DOWNLOAD_LIST') {
		const items = Array.isArray(props?.items) ? props.items : [];
		return (
			<div>
				{commonHeader}
				<Space direction="vertical" style={{ width: '100%' }}>
					{items.length ? (
						items.map((it, idx) => (
							<Card key={idx} size="small">
								<Row align="middle" justify="space-between">
									<Col>
										<Text strong>
											{pickLang(it?.names, lang) || it?.file?.originalName || 'File'}
										</Text>
										<div>
											<Text type="secondary" style={{ fontSize: 12 }}>
												{it?.file?.mime || 'mime'}{' '}
												{it?.file?.size ? `• ${it.file.size} bytes` : ''}
											</Text>
										</div>
									</Col>
									<Col>
										<Button type="primary">Download</Button>
									</Col>
								</Row>
							</Card>
						))
					) : (
						<Text type="secondary">Chưa có file trong danh sách</Text>
					)}
				</Space>
			</div>
		);
	}

	if (type === 'PROJECT_INTRO_2BLOCK') {
		const top = props?.top || {};
		const bottom = props?.bottom || {};

		const blockView = (b, reverse = false) => {
			if (b?.enabled === false) return null;
			const img = b?.image?.url;
			const html = pickLang(b?.html, lang);
			const heading = pickLang(b?.heading, lang);

			return (
				<Row gutter={12} style={{ marginBottom: 14 }} align="middle">
					<Col span={12} order={reverse ? 2 : 1}>
						<Card size="small" title={heading || undefined}>
							{html ? (
								<div dangerouslySetInnerHTML={{ __html: html }} />
							) : (
								<Text type="secondary">Chưa có nội dung</Text>
							)}
						</Card>
					</Col>
					<Col span={12} order={reverse ? 1 : 2}>
						<div
							style={{
								height: 170,
								borderRadius: 12,
								border: '1px solid #f0f0f0',
								overflow: 'hidden',
								background: '#fafafa',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							{img ? (
								<img
									src={img}
									alt="intro"
									style={{ width: '100%', height: '100%', objectFit: 'cover' }}
								/>
							) : (
								<Text type="secondary">No Image</Text>
							)}
						</div>
					</Col>
				</Row>
			);
		};

		return (
			<div>
				{commonHeader}
				{blockView(top, false)}
				{blockView(bottom, true)}
			</div>
		);
	}

	if (type === 'PRODUCT_SWIPER') {
		const q = props?.query || {};
		return (
			<div>
				{commonHeader}
				<Text type="secondary">
					Data source (query): defaultPage={q.defaultPage ?? 1}, defaultLimit={q.defaultLimit ?? 12}
				</Text>
				<Divider style={{ margin: '10px 0' }} />
				<Row gutter={[12, 12]}>
					{Array.from({ length: 6 }).map((_, i) => (
						<Col span={8} key={i}>
							<Card size="small" hoverable>
								<div style={{ height: 110, background: '#fafafa', borderRadius: 10 }} />
								<div style={{ marginTop: 10 }}>
									<Text strong>Product #{i + 1}</Text>
									<div style={{ marginTop: 8 }}>
										<Button type="primary" block>
											Xem thêm
										</Button>
									</div>
								</div>
							</Card>
						</Col>
					))}
				</Row>
			</div>
		);
	}

	if (type === 'NEWS_SWIPER') {
		const q = props?.query || {};
		return (
			<div>
				{commonHeader}
				<Text type="secondary">
					Data source (query): defaultPage={q.defaultPage ?? 1}, defaultLimit={q.defaultLimit ?? 12}
				</Text>
				<Divider style={{ margin: '10px 0' }} />
				<Row gutter={[12, 12]}>
					{Array.from({ length: 6 }).map((_, i) => (
						<Col span={8} key={i}>
							<Card size="small" hoverable>
								<div style={{ height: 110, background: '#fafafa', borderRadius: 10 }} />
								<div style={{ marginTop: 10 }}>
									<Text type="secondary" style={{ fontSize: 12 }}>
										18.06.2025 / CATEGORY
									</Text>
									<div>
										<Text strong>News title #{i + 1}</Text>
									</div>
									<div style={{ marginTop: 8, textAlign: 'right' }}>
										<Text strong>MORE +</Text>
									</div>
								</div>
							</Card>
						</Col>
					))}
				</Row>
			</div>
		);
	}

	if (type === 'NEWS_HERO_PLUS_3') {
		const q = props?.query || {};
		return (
			<div>
				{commonHeader}
				<Text type="secondary">Data source (query): defaultLimit={q.defaultLimit ?? 4} (Hero + 3)</Text>
				<Divider style={{ margin: '10px 0' }} />
				<Row gutter={12}>
					<Col span={14}>
						<Card hoverable>
							<div style={{ height: 220, background: '#fafafa', borderRadius: 12 }} />
							<div style={{ marginTop: 10 }}>
								<Text type="secondary">18.06.2025 / CATEGORY</Text>
								<div>
									<Title level={5} style={{ margin: '6px 0' }}>
										Hero News Title
									</Title>
								</div>
								<Text type="secondary">Mô tả ngắn...</Text>
							</div>
						</Card>
					</Col>
					<Col span={10}>
						<Card size="small" title="Tin mới nhất">
							<Space direction="vertical" style={{ width: '100%' }}>
								{Array.from({ length: 3 }).map((_, i) => (
									<div key={i} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
										<Text type="secondary">12.06.2025 / CATEGORY</Text>
										<div>
											<Text strong>News #{i + 1}</Text>
										</div>
										<div style={{ textAlign: 'right' }}>
											<Text strong>MORE +</Text>
										</div>
									</div>
								))}
							</Space>
						</Card>
					</Col>
				</Row>
			</div>
		);
	}

	if (type === 'CATEGORY_GRID') {
		const kind = props?.kind || 'product';
		return (
			<div>
				{commonHeader}
				<Text type="secondary">Grid kind: {kind}</Text>
				<Divider style={{ margin: '10px 0' }} />
				<Row gutter={[12, 12]}>
					{Array.from({ length: 4 }).map((_, i) => (
						<Col span={12} key={i}>
							<Card hoverable>
								<div style={{ height: 150, background: '#fafafa', borderRadius: 12 }} />
								<div style={{ marginTop: 10, textAlign: 'center' }}>
									<Text strong>Category #{i + 1}</Text>
									<div style={{ marginTop: 8 }}>
										<Button type="primary">Xem thêm</Button>
									</div>
								</div>
							</Card>
						</Col>
					))}
				</Row>
			</div>
		);
	}

	if (type === 'LINK_CARDS') {
		const items = Array.isArray(props?.items) ? props.items : [];
		return (
			<div>
				{commonHeader}
				<Space direction="vertical" style={{ width: '100%' }}>
					{items.length ? (
						items.map((it, idx) => {
							const img = it?.image?.url;
							const name = pickLang(it?.name, lang) || `Link ${idx + 1}`;
							return (
								<div
									key={idx}
									style={{
										height: 84,
										borderRadius: 14,
										overflow: 'hidden',
										position: 'relative',
										border: '1px solid #f0f0f0',
										background: img ? 'transparent' : '#fafafa',
									}}
								>
									{img ? (
										<img
											src={img}
											alt="bg"
											style={{ width: '100%', height: '100%', objectFit: 'cover' }}
										/>
									) : null}
									<div
										style={{
											position: 'absolute',
											inset: 0,
											background: 'rgba(0,0,0,0.35)',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											padding: '0 16px',
										}}
									>
										<Text style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>{name}</Text>
										<Text style={{ color: '#fff', fontWeight: 700 }}>XEM THÊM +</Text>
									</div>
								</div>
							);
						})
					) : (
						<Text type="secondary">Chưa có link cards</Text>
					)}
				</Space>
			</div>
		);
	}

	return <Text type="secondary">Chưa có preview cho loại này</Text>;
}

// ============================
// Main reusable drawer
// ============================
const SectionEditorDrawer = forwardRef(function SectionEditorDrawer({ width = 1100, onSaved }, ref) {
	const [open, setOpen] = useState(false);
	const [saving, setSaving] = useState(false);

	const [editingId, setEditingId] = useState(null);
	const [editingSec, setEditingSec] = useState(null);
	const [form] = Form.useForm();

	const [currentType, setCurrentType] = useState('CONTENT_HTML');
	const [previewLang, setPreviewLang] = useState('vi');

	const suppressTypeResetRef = useRef(false);
	const resettingRef = useRef(false);

	// page picker (LINK_CARDS)
	const [pagePickerOpen, setPagePickerOpen] = useState(false);
	const [pageLoading, setPageLoading] = useState(false);
	const [pageItems, setPageItems] = useState([]);
	const [pagePagination, setPagePagination] = useState({ page: 1, limit: 10, total: 0 });
	const [pageFilters, setPageFilters] = useState({ search: '', enabled: undefined, template: undefined });
	const [pickTargetIndex, setPickTargetIndex] = useState(null);

	// data sources for selects
	const [projectLoading, setProjectLoading] = useState(false);
	const [projects, setProjects] = useState([]);

	const [productCatLoading, setProductCatLoading] = useState(false);
	const [productCats, setProductCats] = useState([]);

	const [newsCatLoading, setNewsCatLoading] = useState(false);
	const [newsCats, setNewsCats] = useState([]);

	const debounceRef = useRef({});
	const debounce = (key, fn, wait = 350) => {
		if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
		debounceRef.current[key] = setTimeout(fn, wait);
	};

	const projectOptions = useMemo(() => {
		return (projects || []).map((p) => {
			const vi = pickLang(p?.names, 'vi');
			const en = pickLang(p?.names, 'en');
			const name = vi || en || p?.name || p?.slug || p?._id;
			const sub = p?.slug ? ` • ${p.slug}` : '';
			return { value: p._id, label: `${name}${sub}` };
		});
	}, [projects]);

	const productCatOptions = useMemo(() => {
		return (productCats || []).map((c) => {
			const vi = pickLang(c?.names, 'vi');
			const en = pickLang(c?.names, 'en');
			const name = vi || en || c?.name || c?.slug || c?._id;
			const sub = c?.slug ? ` • ${c.slug}` : '';
			return { value: c._id, label: `${name}${sub}` };
		});
	}, [productCats]);

	const newsCatOptions = useMemo(() => {
		return (newsCats || []).map((c) => {
			const vi = pickLang(c?.names, 'vi');
			const en = pickLang(c?.names, 'en');
			const name = vi || en || c?.name || c?.slug || c?._id;
			const sub = c?.slug ? ` • ${c.slug}` : '';
			return { value: c._id, label: `${name}${sub}` };
		});
	}, [newsCats]);

	const loadProjects = async (search = '') => {
		try {
			setProjectLoading(true);
			const res = await apiAdminListProject({ page: 1, limit: 200, search: search || '' });
			const { items } = extractList(res);
			setProjects(items);
		} catch (e) {
			message.error(e?.message || 'Load projects failed');
		} finally {
			setProjectLoading(false);
		}
	};

	const loadProductCategories = async ({ search = '', projectId } = {}) => {
		try {
			setProductCatLoading(true);
			const params = { page: 1, limit: 500, search: search || '' };
			if (projectId) params.projectId = projectId;
			const res = await apiAdminListProductCategories(params);
			const { items } = extractList(res);
			setProductCats(items);
		} catch (e) {
			message.error(e?.message || 'Load product categories failed');
		} finally {
			setProductCatLoading(false);
		}
	};

	const loadNewsCategories = async ({ search = '', projectId } = {}) => {
		try {
			setNewsCatLoading(true);
			const params = { page: 1, limit: 500, search: search || '' };
			if (projectId) params.projectId = projectId;
			const res = await apiAdminListNewsCategories(params);
			const { items } = extractList(res);
			setNewsCats(items);
		} catch (e) {
			message.error(e?.message || 'Load news categories failed');
		} finally {
			setNewsCatLoading(false);
		}
	};

	useEffect(() => {
		// preload nhẹ
		loadProjects('');
		loadProductCategories({});
		loadNewsCategories({});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const setFormSafe = useCallback(
		(vals) => {
			suppressTypeResetRef.current = true;
			resettingRef.current = true;

			form.setFieldsValue(vals);
			setCurrentType(vals?.type || 'CONTENT_HTML');

			Promise.resolve().then(() => {
				resettingRef.current = false;
				suppressTypeResetRef.current = false;
			});
		},
		[form]
	);

	const scheduleResetProps = useCallback(
		(newType) => {
			if (suppressTypeResetRef.current) return;
			if (resettingRef.current) return;

			resettingRef.current = true;
			Promise.resolve().then(() => {
				try {
					form.setFields([{ name: ['props'], value: makeDefaultProps(newType) }]);
				} finally {
					resettingRef.current = false;
				}
			});
		},
		[form]
	);

	const openCreate = async () => {
		setEditingId(null);
		setEditingSec(null);
		setOpen(true);

		setFormSafe({
			name: '',
			type: 'CONTENT_HTML',
			tags: [],
			props: makeDefaultProps('CONTENT_HTML'),
		});
	};

	const openEdit = async (id) => {
		if (!id) return;
		try {
			setEditingId(id);
			setEditingSec(null);
			setOpen(true);

			const res = await apiAdminGetSectionById(id);
			const sec = res?.data?.data || res?.data || null;
			const normalized = normalizeSectionForForm(sec);
			setEditingSec(normalized);
			setFormSafe(normalized);
		} catch (e) {
			message.error(e?.message || 'Load section failed');
			setOpen(false);
			setEditingId(null);
			setEditingSec(null);
		}
	};

	useImperativeHandle(ref, () => ({
		openCreate,
		openEdit,
		close: () => setOpen(false),
	}));

	const save = async (closeAfter = true) => {
		try {
			const values = await form.validateFields();
			const payload = buildPayloadFromForm(values);

			setSaving(true);
			if (editingId) {
				await sectionApiV2.update(editingId, payload);
				message.success('Đã cập nhật khối nội dung');
			} else {
				await sectionApiV2.create(payload);
				message.success('Đã tạo khối nội dung');
			}

			onSaved?.();
			if (closeAfter) setOpen(false);
		} catch (e) {
			if (e?.message) message.error(e.message);
		} finally {
			setSaving(false);
		}
	};

	// ================= page picker (LINK_CARDS) =================
	const fetchPages = async (override = {}) => {
		const nextFilters = { ...pageFilters, ...override.filters };
		const page = override.page ?? pagePagination.page;
		const limit = override.limit ?? pagePagination.limit;

		try {
			setPageLoading(true);
			const res = await apiAdminListPages({
				page,
				limit,
				enabled: nextFilters.enabled,
				template: nextFilters.template,
				search: nextFilters.search || '',
			});

			const data = res?.data?.data || res?.data || {};
			const list = data?.items || [];
			const pg = data?.pagination || {};

			setPageItems(list);
			setPagePagination({ page: pg.page || page, limit: pg.limit || limit, total: pg.total || 0 });
			setPageFilters(nextFilters);
		} catch (e) {
			message.error(e?.message || 'Load pages failed');
		} finally {
			setPageLoading(false);
		}
	};

	const openPagePicker = (idx) => {
		setPickTargetIndex(idx);
		setPagePickerOpen(true);
		fetchPages({ page: 1, limit: 10 });
	};

	const pickPage = (pageDoc) => {
		const id = pageDoc?._id;
		const path = pageDoc?.path || '';
		if (!id || typeof pickTargetIndex !== 'number') return;

		setFieldValueCompat(form, ['props', 'items', pickTargetIndex, 'pageId'], id);
		setFieldValueCompat(form, ['props', 'items', pickTargetIndex, 'href'], path);
		setFieldValueCompat(form, ['props', 'items', pickTargetIndex, 'linkType'], 'page');

		const cur = form.getFieldValue(['props', 'items', pickTargetIndex]) || {};
		if (!cur?.name) setFieldValueCompat(form, ['props', 'items', pickTargetIndex, 'name'], { vi: '', en: '' });
		if (!cur?.image) setFieldValueCompat(form, ['props', 'items', pickTargetIndex, 'image'], null);

		message.success(`Đã chọn page: ${path || id}`);
		setPagePickerOpen(false);
	};

	const pageColumns = [
		{
			title: 'Tên (VI)',
			dataIndex: ['names', 'vi'],
			key: 'vi',
			render: (v, r) => v || r?.names?.en || '(no name)',
		},
		{ title: 'Path', dataIndex: 'path', key: 'path', render: (v) => <Text type="secondary">{v}</Text> },
		{
			title: 'Template',
			dataIndex: 'template',
			key: 'template',
			render: (v) => (v ? <Tag>{v}</Tag> : <Tag>—</Tag>),
		},
		{
			title: 'Enabled',
			dataIndex: 'enabled',
			key: 'enabled',
			render: (v) => (v ? <Tag color="green">ON</Tag> : <Tag color="red">OFF</Tag>),
		},
		{
			title: 'Action',
			key: 'action',
			render: (_, r) => (
				<Button type="primary" size="small" onClick={() => pickPage(r)}>
					Chọn
				</Button>
			),
		},
	];

	// ================= editor render per type =================
	const renderProjectSelect = (namePath, { placeholder = 'Chọn dự án', onPicked } = {}) => (
		<Form.Item name={namePath} label="Dự án (projectId)" tooltip="Chọn 1 dự án (optional)">
			<Select
				allowClear
				showSearch
				placeholder={placeholder}
				options={projectOptions}
				loading={projectLoading}
				filterOption={false}
				onDropdownVisibleChange={(o) => {
					if (o && !projects.length) loadProjects('');
				}}
				onSearch={(val) => debounce('projectSearch', () => loadProjects(val || ''))}
				onChange={(val) => onPicked?.(val || null)}
			/>
		</Form.Item>
	);

	const renderProductCategoryMultiSelect = (namePath, { projectId } = {}) => (
		<Form.Item name={namePath} label="Danh mục sản phẩm (productCategoryIds)" tooltip="Chọn 1 hoặc nhiều">
			<Select
				mode="multiple"
				allowClear
				showSearch
				placeholder="Chọn danh mục sản phẩm"
				options={productCatOptions}
				loading={productCatLoading}
				filterOption={false}
				onDropdownVisibleChange={(o) => {
					if (o && !productCats.length) loadProductCategories({ projectId });
				}}
				onSearch={(val) =>
					debounce('productCatSearch', () => loadProductCategories({ search: val || '', projectId }))
				}
			/>
		</Form.Item>
	);

	const renderNewsCategoryMultiSelect = (namePath, { projectId } = {}) => (
		<Form.Item name={namePath} label="Danh mục tin tức (newsCategoryIds)" tooltip="Chọn 1 hoặc nhiều">
			<Select
				mode="multiple"
				allowClear
				showSearch
				placeholder="Chọn danh mục tin tức"
				options={newsCatOptions}
				loading={newsCatLoading}
				filterOption={false}
				onDropdownVisibleChange={(o) => {
					if (o && !newsCats.length) loadNewsCategories({ projectId });
				}}
				onSearch={(val) =>
					debounce('newsCatSearch', () => loadNewsCategories({ search: val || '', projectId }))
				}
			/>
		</Form.Item>
	);

	const renderPropsEditor = () => {
		const type = currentType;

		if (type === 'CONTENT_HTML') {
			return (
				<>
					<I18nTabsInputSafe form={form} label="Tiêu đề" namePath={['props', 'title']} />
					<I18nTabsEditorSafe form={form} label="Mô tả" namePath={['props', 'description']} />
					<I18nTabsEditorSafe form={form} label="HTML" namePath={['props', 'html']} required />
					<Form.Item name={['props', 'boxed']} label="Boxed" valuePropName="checked">
						<Switch />
					</Form.Item>
				</>
			);
		}

		if (type === 'DOWNLOAD_LIST') {
			const uploadFileProps = (field) => ({
				multiple: false,
				maxCount: 1,
				showUploadList: false,
				customRequest: async (options) => {
					const { file, onSuccess, onError } = options;
					try {
						const data = await apiUploadCloudFile(file, 'files');
						form.setFields([
							{ name: ['props', 'items', field.name, 'file', 'url'], value: data?.url || '' },
							{
								name: ['props', 'items', field.name, 'file', 'originalName'],
								value: data?.originalName || file?.name || '',
							},
							{ name: ['props', 'items', field.name, 'file', 'mime'], value: data?.mime || '' },
							{ name: ['props', 'items', field.name, 'file', 'size'], value: data?.size ?? undefined },
						]);
						message.success('Upload file thành công');
						onSuccess?.(data);
					} catch (e) {
						message.error(e?.message || 'Upload file lỗi');
						onError?.(e);
					}
				},
			});

			return (
				<>
					<I18nTabsInputSafe form={form} label="Tiêu đề" namePath={['props', 'title']} />
					<I18nTabsEditorSafe form={form} label="Mô tả" namePath={['props', 'description']} />

					<Divider />

					<Form.List name={['props', 'items']}>
						{(fields, { add, remove }) => (
							<>
								<Row justify="space-between" align="middle">
									<Col>
										<Text strong>Download Items</Text>
									</Col>
									<Col>
										<Button onClick={() => add({ names: { vi: '', en: '' }, file: { url: '' } })}>
											Thêm item
										</Button>
									</Col>
								</Row>

								<div style={{ marginTop: 10 }}>
									{fields.map((field) => (
										<Card
											key={field.key}
											size="small"
											style={{ marginBottom: 10 }}
											title={<Text strong>Item #{field.name + 1}</Text>}
											extra={
												<Button danger size="small" onClick={() => remove(field.name)}>
													Xóa
												</Button>
											}
										>
											<I18nTabsInputSafe
												form={form}
												label="Names"
												namePath={['props', 'items', field.name, 'names']}
												formNamePath={[field.name, 'names']}
												required
											/>

											<Divider style={{ margin: '12px 0' }} />

											<Space wrap>
												<Upload {...uploadFileProps(field)}>
													<Button icon={<CloudUploadOutlined />}>
														Upload File (optional)
													</Button>
												</Upload>
												<Text type="secondary">
													Nếu backend không có upload file thì nhập url thủ công.
												</Text>
											</Space>

											<Row gutter={12} style={{ marginTop: 10 }}>
												<Col span={24}>
													<Form.Item
														name={[field.name, 'file', 'url']}
														label="file.url (required)"
														rules={[{ required: true, message: 'Nhập file.url' }]}
													>
														<Input placeholder="https://.../file.xlsx" />
													</Form.Item>
												</Col>
												<Col span={12}>
													<Form.Item
														name={[field.name, 'file', 'originalName']}
														label="file.originalName"
													>
														<Input />
													</Form.Item>
												</Col>
												<Col span={12}>
													<Form.Item name={[field.name, 'file', 'mime']} label="file.mime">
														<Input />
													</Form.Item>
												</Col>
												<Col span={12}>
													<Form.Item name={[field.name, 'file', 'size']} label="file.size">
														<InputNumber style={{ width: '100%' }} min={0} />
													</Form.Item>
												</Col>
											</Row>
										</Card>
									))}
									{!fields.length ? <Text type="secondary">Chưa có item</Text> : null}
								</div>
							</>
						)}
					</Form.List>
				</>
			);
		}

		if (type === 'PROJECT_INTRO_2BLOCK') {
			const BlockEditor = ({ blockKey, title }) => (
				<Card size="small" style={{ marginBottom: 12 }} title={title}>
					<Form.Item name={['props', blockKey, 'enabled']} label="Enabled" valuePropName="checked">
						<Switch />
					</Form.Item>

					<ImagePicker form={form} namePath={['props', blockKey, 'image']} label="Image (optional)" />
					<I18nTabsInputSafe form={form} label="Heading" namePath={['props', blockKey, 'heading']} />
					<I18nTabsEditorSafe form={form} label="HTML" namePath={['props', blockKey, 'html']} />
				</Card>
			);

			return (
				<>
					<I18nTabsInputSafe form={form} label="Tiêu đề" namePath={['props', 'title']} />
					<I18nTabsEditorSafe form={form} label="Mô tả" namePath={['props', 'description']} />
					<Form.Item name={['props', 'boxed']} label="Boxed" valuePropName="checked">
						<Switch />
					</Form.Item>

					<Divider />
					<BlockEditor blockKey="top" title="Top block" />
					<BlockEditor blockKey="bottom" title="Bottom block" />
				</>
			);
		}

		if (type === 'PRODUCT_SWIPER') {
			return (
				<>
					<I18nTabsInputSafe form={form} label="Tiêu đề" namePath={['props', 'title']} />
					<I18nTabsEditorSafe form={form} label="Mô tả" namePath={['props', 'description']} />

					<Divider />
					<Text strong>Query</Text>

					<Form.Item noStyle shouldUpdate>
						{() => {
							const projectId = form.getFieldValue(['props', 'query', 'projectId']) || null;

							return (
								<Row gutter={12} style={{ marginTop: 10 }}>
									<Col span={24}>
										{renderProjectSelect(['props', 'query', 'projectId'], {
											onPicked: (val) =>
												loadProductCategories({ projectId: val || null, search: '' }),
										})}
									</Col>
									<Col span={24}>
										{renderProductCategoryMultiSelect(['props', 'query', 'productCategoryIds'], {
											projectId,
										})}
									</Col>
									<Col span={12}>
										<Form.Item name={['props', 'query', 'defaultPage']} label="defaultPage">
											<InputNumber style={{ width: '100%' }} min={1} />
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item name={['props', 'query', 'defaultLimit']} label="defaultLimit">
											<InputNumber style={{ width: '100%' }} min={1} />
										</Form.Item>
									</Col>
								</Row>
							);
						}}
					</Form.Item>
				</>
			);
		}

		if (type === 'NEWS_SWIPER' || type === 'NEWS_HERO_PLUS_3') {
			const limitLabel = type === 'NEWS_HERO_PLUS_3' ? 'defaultLimit (Hero+3)' : 'defaultLimit';
			return (
				<>
					<I18nTabsInputSafe form={form} label="Tiêu đề" namePath={['props', 'title']} />
					<I18nTabsEditorSafe form={form} label="Mô tả" namePath={['props', 'description']} />

					<Divider />
					<Text strong>Query</Text>

					<Form.Item noStyle shouldUpdate>
						{() => {
							const projectId = form.getFieldValue(['props', 'query', 'projectId']) || null;

							return (
								<Row gutter={12} style={{ marginTop: 10 }}>
									<Col span={24}>
										{renderProjectSelect(['props', 'query', 'projectId'], {
											onPicked: (val) =>
												loadNewsCategories({ projectId: val || null, search: '' }),
										})}
									</Col>
									<Col span={24}>
										{renderNewsCategoryMultiSelect(['props', 'query', 'newsCategoryIds'], {
											projectId,
										})}
									</Col>
									<Col span={12}>
										<Form.Item name={['props', 'query', 'defaultPage']} label="defaultPage">
											<InputNumber style={{ width: '100%' }} min={1} />
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item name={['props', 'query', 'defaultLimit']} label={limitLabel}>
											<InputNumber style={{ width: '100%' }} min={1} />
										</Form.Item>
									</Col>
								</Row>
							);
						}}
					</Form.Item>
				</>
			);
		}

		if (type === 'CATEGORY_GRID') {
			return (
				<>
					<I18nTabsInputSafe form={form} label="Tiêu đề" namePath={['props', 'title']} />
					<I18nTabsEditorSafe form={form} label="Mô tả" namePath={['props', 'description']} />

					<Row gutter={12}>
						<Col span={12}>
							<Form.Item name={['props', 'kind']} label="kind">
								<Select
									options={[
										{ value: 'product', label: 'product' },
										{ value: 'news', label: 'news' },
									]}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							{renderProjectSelect(['props', 'projectId'], { placeholder: 'Chọn dự án (lọc grid)' })}
						</Col>
						<Col span={24}>
							<Form.Item
								name={['props', 'basePath']}
								label="basePath"
								tooltip='VD: "/product/list" hoặc "/news/list"'
							>
								<Input placeholder="/product/list" />
							</Form.Item>
						</Col>
					</Row>
				</>
			);
		}

		if (type === 'LINK_CARDS') {
			return (
				<>
					<I18nTabsInputSafe form={form} label="Tiêu đề" namePath={['props', 'title']} />
					<I18nTabsEditorSafe form={form} label="Mô tả" namePath={['props', 'description']} />

					<Divider />

					<Form.List name={['props', 'items']}>
						{(fields, { add, remove }) => (
							<>
								<Row justify="space-between" align="middle">
									<Col>
										<Text strong>Link Card Items</Text>
									</Col>
									<Col>
										<Button
											onClick={() =>
												add({
													name: { vi: '', en: '' },
													image: null,
													href: '',
													pageId: null,
													linkType: 'url',
												})
											}
										>
											Thêm item
										</Button>
									</Col>
								</Row>

								<div style={{ marginTop: 10 }}>
									{fields.map((field) => (
										<Card
											key={field.key}
											size="small"
											style={{ marginBottom: 10 }}
											title={<Text strong>Item #{field.name + 1}</Text>}
											extra={
												<Button danger size="small" onClick={() => remove(field.name)}>
													Xóa
												</Button>
											}
										>
											<I18nTabsInputSafe
												form={form}
												label="Name"
												namePath={['props', 'items', field.name, 'name']}
												formNamePath={[field.name, 'name']}
												required
											/>

											<Form.Item name={[field.name, 'linkType']} hidden>
												<Input />
											</Form.Item>

											<Form.Item label="Loại đường dẫn" required shouldUpdate>
												{() => {
													const linkTypePathAbs = ['props', 'items', field.name, 'linkType'];
													const pageIdPathAbs = ['props', 'items', field.name, 'pageId'];
													const linkType = form.getFieldValue(linkTypePathAbs) ?? 'url';

													return (
														<Radio.Group
															value={linkType}
															optionType="button"
															buttonStyle="solid"
															options={[
																{ label: 'URL', value: 'url' },
																{ label: 'PAGE', value: 'page' },
															]}
															onChange={(e) => {
																const t = e.target.value;
																setFieldValueCompat(form, linkTypePathAbs, t);
																if (t === 'url')
																	setFieldValueCompat(form, pageIdPathAbs, null);
															}}
														/>
													);
												}}
											</Form.Item>

											<Form.Item shouldUpdate noStyle>
												{() => {
													const t =
														form.getFieldValue([
															'props',
															'items',
															field.name,
															'linkType',
														]) || 'url';
													return t === 'url' ? (
														<Form.Item
															name={[field.name, 'href']}
															label="href"
															tooltip="FE đang dùng href để link"
														>
															<Input placeholder="/ve-chung-toi hoặc https://..." />
														</Form.Item>
													) : (
														<>
															<Row
																gutter={12}
																align="middle"
																style={{ marginBottom: 12 }}
															>
																<Col span={10}>
																	<Button
																		icon={<SearchOutlined />}
																		onClick={() => openPagePicker(field.name)}
																		block
																	>
																		Chọn Page
																	</Button>
																</Col>
															</Row>

															<Form.Item
																name={[field.name, 'href']}
																label="href (auto set = page.path)"
																tooltip="Vẫn dùng href để FE chạy, nhưng set sẵn từ page.path cho tiện"
															>
																<Input placeholder="/page/slug" />
															</Form.Item>
														</>
													);
												}}
											</Form.Item>

											<ImagePicker
												form={form}
												namePath={['props', 'items', field.name, 'image']}
												formNamePath={[field.name, 'image']}
												label="Image (optional)"
											/>
										</Card>
									))}
									{!fields.length ? <Text type="secondary">Chưa có item</Text> : null}
								</div>
							</>
						)}
					</Form.List>
				</>
			);
		}

		return <Text type="secondary">Loại khối nội dung chưa hỗ trợ editor</Text>;
	};

	return (
		<>
			<Drawer
				title={editingId ? `Sửa Section: ${editingSec?.name || editingId}` : 'Thêm Section'}
				open={open}
				width={width}
				onClose={() => setOpen(false)}
				destroyOnHidden
				footer={
					<Space style={{ float: 'right' }}>
						<Button onClick={() => setOpen(false)}>Cancel</Button>
						<Button onClick={() => save(false)} loading={saving}>
							Save (keep open)
						</Button>
						<Button type="primary" onClick={() => save(true)} loading={saving}>
							Save
						</Button>
					</Space>
				}
				extra={
					<Button icon={<ReloadOutlined />} onClick={() => (editingId ? openEdit(editingId) : openCreate())}>
						Reload
					</Button>
				}
			>
				<Form
					form={form}
					layout="vertical"
					initialValues={{ type: 'CONTENT_HTML', tags: [] }}
					onValuesChange={(changed) => {
						if (resettingRef.current) return;
						if (changed?.type) {
							setCurrentType(changed.type);
							scheduleResetProps(changed.type);
						}
					}}
				>
					<Row gutter={16}>
						<Col xs={24} lg={12}>
							<Row gutter={12}>
								<Col span={16}>
									<Form.Item name="name" label="Tên nội bộ" tooltip="Tên nội bộ để dễ tìm">
										<Input placeholder="VD: Home hero, About intro..." />
									</Form.Item>
								</Col>
								<Col span={8}>
									<Form.Item
										name="type"
										label="Loại khối nội dung"
										rules={[{ required: true, message: 'Chọn khối nội dung' }]}
									>
										<Select
											options={SECTION_TYPE_OPTIONS.map((t) => ({
												value: t.value,
												label: t.label,
											}))}
										/>
									</Form.Item>
								</Col>
							</Row>

							<Form.Item name="tags" label="Tags" tooltip="Dùng để filter library (optional)">
								<Select mode="tags" placeholder="Nhập tag rồi Enter" />
							</Form.Item>

							<Divider />

							<Card size="small" title="Thuộc tính (theo loại)">
								{renderPropsEditor()}
							</Card>
						</Col>

						<Col xs={24} lg={12}>
							<Card
								title={
									<Space>
										<EyeOutlined />
										<span>Preview UI</span>
										{currentType ? renderTypeTag(currentType) : null}
										<Divider type="vertical" />
										<Radio.Group
											value={previewLang}
											optionType="button"
											buttonStyle="solid"
											onChange={(e) => setPreviewLang(e.target.value)}
											options={[
												{ label: 'VI', value: 'vi' },
												{ label: 'EN', value: 'en' },
											]}
										/>
									</Space>
								}
							>
								<Form.Item noStyle shouldUpdate>
									{() => {
										const type = form.getFieldValue('type');
										const props = form.getFieldValue('props') || {};
										const fullValues = form.getFieldsValue(true);

										return (
											<>
												<PreviewSection type={type} props={props} lang={previewLang} />
												<Divider />
												<Text type="secondary">JSON payload (debug)</Text>
												<pre
													style={{
														marginTop: 8,
														background: '#111',
														color: '#0f0',
														padding: 12,
														borderRadius: 10,
														maxHeight: 340,
														overflow: 'auto',
													}}
												>
													{JSON.stringify(buildPayloadFromForm(fullValues), null, 2)}
												</pre>
											</>
										);
									}}
								</Form.Item>
							</Card>
						</Col>
					</Row>
				</Form>
			</Drawer>

			{/* Page Picker Modal */}
			<Modal
				title="Chọn Page (pages/admin)"
				open={pagePickerOpen}
				onCancel={() => setPagePickerOpen(false)}
				footer={null}
				width={1000}
			>
				<Row gutter={[12, 12]} align="middle">
					<Col xs={24} md={10}>
						<Input
							allowClear
							prefix={<SearchOutlined />}
							placeholder="Search path / names.vi / names.en"
							value={pageFilters.search}
							onChange={(e) => setPageFilters((p) => ({ ...p, search: e.target.value }))}
							onPressEnter={() => fetchPages({ page: 1 })}
						/>
					</Col>

					<Col xs={24} md={6}>
						<Select
							style={{ width: '100%' }}
							allowClear
							placeholder="Template"
							value={pageFilters.template}
							onChange={(v) => {
								const next = { ...pageFilters, template: v };
								setPageFilters(next);
								fetchPages({ page: 1, filters: next });
							}}
							options={[
								{ value: 'SERVICE', label: 'SERVICE' },
								{ value: 'PROJECT', label: 'PROJECT' },
								{ value: 'NEWS_LIST', label: 'NEWS_LIST' },
								{ value: 'PRODUCT_LIST', label: 'PRODUCT_LIST' },
								{ value: 'PRODUCT_GROUP', label: 'PRODUCT_GROUP' },
							]}
						/>
					</Col>

					<Col xs={24} md={4}>
						<Select
							style={{ width: '100%' }}
							allowClear
							placeholder="Enabled"
							value={pageFilters.enabled}
							onChange={(v) => {
								const next = { ...pageFilters, enabled: v };
								setPageFilters(next);
								fetchPages({ page: 1, filters: next });
							}}
							options={[
								{ value: true, label: 'ON' },
								{ value: false, label: 'OFF' },
							]}
						/>
					</Col>

					<Col xs={24} md={4}>
						<Button type="primary" icon={<SearchOutlined />} block onClick={() => fetchPages({ page: 1 })}>
							Tìm
						</Button>
					</Col>
				</Row>

				<Divider style={{ margin: '12px 0' }} />

				<Spin spinning={pageLoading}>
					<Table rowKey="_id" columns={pageColumns} dataSource={pageItems} pagination={false} size="middle" />
				</Spin>

				<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
					<Pagination
						current={pagePagination.page}
						pageSize={pagePagination.limit}
						total={pagePagination.total}
						showSizeChanger
						pageSizeOptions={[10, 20, 50, 100]}
						onChange={(page, pageSize) => fetchPages({ page, limit: pageSize })}
					/>
				</div>
			</Modal>
		</>
	);
});

export default SectionEditorDrawer;
